import rp from "@reporting/rp-app-host";
import { TreeEvent, TreeMenu } from "@omegagrid/tree";
import { dom } from "@omegagrid/core";
import { msg } from "@omegagrid/localize";
import { html, css } from "lit";
import { ModuleCode } from "@reporting/rp-types/accounting";
import { ReportContext } from "@reporting/rp-types/analytics";
import { ModifyContextParams } from "../../types";
import { getDimensionMenuItems, getDimensionName, getDrillContainerId, openDrillDialog } from "./drillDown";
import type { ChartPointEvent, ChartVisualization } from "./chart";

/**
 * Drill down info attached to the chart points (point.custom, see
 * ChartFactory) - the dimension filters of the point axis items, the
 * indicator of the value and the dialog caption.
 */
export type ChartPointDrill = {
	filters: Record<string, string>;
	indicator?: number;
	description: string;
};

const DETAIL_KEY = '__detail';

/**
 * Drill down of the chart points, the counterpart of the TableController
 * drills. A click on a point opens the menu with the transaction detail of
 * the clicked value and the group by dimensions - both open a new report of
 * the context narrowed down by the point filters (dialog by default, the
 * folder button of a dimension opens a tab).
 */
export class ChartController {

	private _drill: ChartPointDrill;
	private _menu: TreeMenu;

	get menu() {
		if (this._menu) return this._menu;

		this._menu = dom.createElement<TreeMenu>('og-tree-menu', {
			style: {width: '250px', zIndex: '1000'},
			listeners: [['select', (e: TreeEvent) => {
				if (e.node.key === DETAIL_KEY) this.openDetail();
				else this.openGroupBy(e.node.key, 'dialog');
			}]]
		});

		this._menu.setSourceData([
			{k: DETAIL_KEY, v: msg('Transaction detail'), icon: 'list', level: 0},
			...getDimensionMenuItems(this.context.module as ModuleCode),
		], {
			checkboxes: false,
			selectMode: 'single',
			customStyle: css``,
			nodeRenderer: (div, node) => node.key === DETAIL_KEY ? html`
				<div style="flex: 1">${node.value}</div>
			` : html`
				<div style="flex: 1">${node.value}</div>
				<og-button icon="folder-closed" color="transparent" style="width: 20px" @mousedown="${(e: MouseEvent) => {
					e.stopPropagation();
					dom.hideElement(this._menu);
					this.openGroupBy(node.key, 'tab');
				}}"></og-button>
			`,
		});

		rp.host.append(this._menu);

		return this._menu;
	}

	openGroupBy(dim: string, target: 'dialog'|'tab' = 'dialog') {
		const params: ModifyContextParams = {
			filters: this._drill?.filters ?? {},
			dim: dim
		};

		let caption = this._drill?.description ?? '';
		const dimensionName = getDimensionName(this.context.module as ModuleCode, dim);
		if (dimensionName) caption = caption ? `${dimensionName} › ${caption}` : dimensionName;

		const id = getDrillContainerId(this.context, params);

		if (target == 'tab') rp.host.open(id, {title: caption});
		else openDrillDialog(id, caption);
	}

	openDetail() {
		const params: ModifyContextParams = {filters: this._drill?.filters ?? {}};
		if (this._drill?.indicator != null) params.indicator = this._drill.indicator;

		openDrillDialog(getDrillContainerId(this.context, params), this._drill?.description ?? '');
	}

	constructor(readonly chart: ChartVisualization, readonly context: ReportContext) {
		chart.addEventListener('point.click', (async (e: ChartPointEvent) => {
			const drill = e.point?.options?.custom as ChartPointDrill;
			if (!drill) return;
			this._drill = drill;
			await this.menu.updateComplete;
			this.menu.openAt(e.browserEvent.pageX, e.browserEvent.pageY);
		}) as EventListener);

		chart.addEventListener('disconnected', () => {
			this._menu?.remove();
			this._menu = null;
		});
	}

}
